import './styles.scss'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { InputText } from 'primereact/inputtext'
import { ButtonComponent, DialogModal } from 'Components/UIComponents'
import Loader from 'Components/Loader'
import DialogUIModal from 'Components/UIComponents/DialogUIModal'
import apiAdapterCoffeeWeb from 'Services/apiAdapterCoffeeWeb'
import Validation from 'Utils/Validation'

const EditCountryWiseExportReport = ({ setLoading, showEditModal, setShowEditModal, rowData, country, activeYear, fileName, handleReportDataFunction }) => {
  const { t } = useTranslation()
  const [yearData, setYearData] = useState('')
  const [formData, setFormData] = useState({})
  const [errors, setErrors] = useState({})
  const [buttonDisabled, setButtonDisabled] = useState(true)
  const [errorField, setErrorField] = useState(false)
  const [isFormEnabled, setIsFormEnabled] = useState({
    editMode: false,
    submitButton: false
  })
  const [showDialog, setShowDialog] = useState({ showModal: false, modalType: '', message: '', onHide: () => {}, onRetry: () => {} })

  useEffect(() => {
    setYearData(activeYear)

    if (rowData) {
      setFormData({ ...rowData })
    }
  }, [rowData, showEditModal])

  if (!rowData) {
    return null
  }

  const updateExportReportForCountry = async () => {
    handleDismiss()
    setLoading(true)

    try {
      const data = {
        year: yearData,
        fileName: fileName,
        report: [formData]
      }

      const response = await apiAdapterCoffeeWeb.updateCountryWiseExportData(data)

      if (response.status === 200) {
        handleReportDataFunction(response)

        setButtonDisabled(true)
        setShowDialog({
          ...showDialog,
          showModal: true,
          modalType: 'success',
          message: t('REPORT_DATA_UPDATED_SUCCESSFULLY'),
          onHide: handleDismiss,
          onSuccess: handleDismiss
        })
        setShowEditModal(false)
        setIsFormEnabled({
          editMode: false,
          submitButton: false
        })
      }
    } catch {
      setShowDialog({
        ...showDialog,
        showModal: true,
        modalType: 'error',
        message: t('SORRY_COULD_NOT_UPDATE_REPORT_DATA_PLEASE_TRY_AGAIN_LATER'),
        onRetry: updateExportReportForCountry,
        onHide: handleDismiss
      })
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (value, field) => {
    setButtonDisabled(false)

    const isFieldValid = field !== 'month' && field !== 'MONTH'
    const updatedFormData = { ...formData, [field]: value }

    if (isFieldValid && Validation.numberDotPlusAndMinusValidation(value)) {
      setFormData(updatedFormData)
    } else if (!isFieldValid) {
      setFormData(updatedFormData)
    }

    setErrors({
      ...errors,
      [field]: ''
    })
  }

  const handleBlur = (field) => {
    const value = String(formData[field] || '')

    if (value?.trim() === '') {
      setErrors({
        ...errors,
        [field]: `${field.charAt(0).toUpperCase() + field.slice(1).toLowerCase()} ${t('IS_REQUIRED')}`
      })
    }
  }

  const checkFieldsEmpty = () => {
    const allFieldsFilled = Object.keys(formData).every((field) => {
      const value = String(formData[field] || '').trim()

      return field === 'month' || field === 'MONTH' || value !== ''
    })

    return allFieldsFilled
  }

  const closeEditModal = () => {
    setButtonDisabled(true)
    setShowEditModal(false)
    setErrors({})
    setIsFormEnabled({
      editMode: false,
      submitButton: false
    })
  }

  const enableFormEditing = () => {
    setIsFormEnabled({
      editMode: true,
      submitButton: true
    })
  }

  const showResetConfirmationDialog = () => {
    setShowDialog({
      ...showDialog,
      showModal: true,
      modalType: 'info',
      message: t('ARE_YOU_SURE_YOU_WANT_TO_PROCEED'),
      onConfirmation: confirmResetForm,
      onHide: handleDismiss
    })
  }

  const confirmResetForm = () => {
    setButtonDisabled(true)
    if (rowData) {
      setFormData({ ...rowData })
    }

    setIsFormEnabled({
      editMode: false,
      submitButton: false
    })
    setYearData(activeYear)
    setErrors({})
    setErrorField(false)
    handleDismiss()
  }

  const handleDismiss = () => {
    setShowDialog({ ...showDialog, showModal: false })
  }

  const handleInputChange = (value, field) => {
    if (field === 'year') {
      setYearData(value)
    }
    setErrorField(false)
    setButtonDisabled(false)
  }

  const handleInputBlur = (field) => {
    setErrorField((previousData) => ({
      ...previousData,
      [field]: true
    }))
  }
  const editFooterContent = () => (
    <div className="edit-country-export-footer">
      {isFormEnabled.submitButton && (
        <>
          <ButtonComponent type={'cancel'} onClick={closeEditModal} />
          <ButtonComponent type={'submit'} onClick={updateExportReportForCountry} disabled={buttonDisabled || !checkFieldsEmpty() || !yearData} />
        </>
      )}
    </div>
  )

  const renderCountryWiseExportEditForm = () => (
    <div className="export-country-dynamic-edit-form">
      <div className="action-button">{isFormEnabled.editMode ? <ButtonComponent type={'reset'} onClick={showResetConfirmationDialog} /> : <ButtonComponent type={'edit'} onClick={enableFormEditing} />}</div>

      <div className="edit-input-form">
        <div className="input-wrapper">
          <span className="p-float-label">
            <InputText
              name="year"
              value={yearData}
              onChange={(event) => handleInputChange(event.target.value, 'year')}
              onBlur={() => {
                handleInputBlur('year')
              }}
              className={`input-fields ${errorField && !yearData ? 'error-fields' : ''}`}
              disabled={!isFormEnabled.editMode}
              data-testid="input-year"
            />
            <label className={`field-label ${errorField && !yearData ? 'error-field-label' : ''}`}>
              {t('YEAR_FIELD')}
              <span className="required-text-field">*</span>
            </label>
          </span>
          {errorField && !yearData && <small className="error-message">{t('YEAR_FIELD_ERROR_MESSAGE')}</small>}
        </div>

        {Object.keys(rowData).map((field, index) => (
          <div key={index} className="input-wrapper">
            <span className="p-float-label">
              <InputText id={`${field}-${index}`} value={formData[field]} onChange={(event) => handleChange(event.target.value, field)} onBlur={() => handleBlur(field)} className={`input-fields ${errors[field] ? 'error-fields' : ''}`} disabled={field === 'month' || field === 'MONTH' || !isFormEnabled.editMode} data-testid={`input-${field}`} />
              <label htmlFor={`${field}-${index}`} className={`field-label ${errors[field] ? 'error-field-label' : ''}`}>
                {field.charAt(0).toUpperCase() + field.slice(1).toLowerCase()}
                <span className="required-text-field">*</span>
              </label>
            </span>
            <div className="error-message"> {errors[field] && <small>{errors[field]}</small>}</div>
          </div>
        ))}
      </div>
    </div>
  )

  return (
    <>
      <DialogUIModal visible={showEditModal} onHide={closeEditModal} header={`${t('EDIT_COUNTRY_WISE_EXPORT_REPORT')} - ${country?.nicename}`} footer={editFooterContent} body={renderCountryWiseExportEditForm} className="country-wise-export-edit-modal" />

      <DialogModal {...showDialog} onDismiss={handleDismiss} />
    </>
  )
}

export default Loader(EditCountryWiseExportReport)
